import type { Industry } from './account.js';
import type { IsoTimestamp } from './common.js';
import type { HostedReportState } from './hostedReport.js';

// CLIENT PORTAL — THE LIBRARY (Strategy decks + Kyck college).
// Brief: _architect/briefs/2026-09-23-portal-library.md (+ Amendments 1 and 2).
//
// ⚠️⚠️ THE ENTRY IS THE TILE; THE HOSTED REPORT IS THE FILE. Two records, two
// lifecycles, on purpose: deleting a tile never touches the hosted report, and
// revoking a hosted report never deletes the tile — it makes the tile
// UNAVAILABLE, which the staff list shows in its own words.
//
// ⚠️ NOTHING IS DENORMALISED FROM THE ACCOUNT OR THE CREATOR. A tile sits in
// front of a client for months, unlike a hosted report, which is a
// point-in-time artefact. Names are resolved at read time (Amendment 2 §B).

/**
 * ⚠️ Round 6 adds two:
 *   - `strategy.campaigns` — "Campaigns & promotions", the ONE strategy section
 *     every non-fitness-franchise client sees INSTEAD of Monthly + Alternate.
 *     ⚠️ A NEW SECTION, NOT A RELABEL of `strategy.monthly`: staff pick it
 *     explicitly, or the publish form would show one name while the client saw
 *     another. Which strategy sections a viewer has is `resolveStrategyNav`
 *     (src/lib/portalStrategyNav.ts).
 *   - `reports.custom` — one-off HTML documents built for a client, group or
 *     brand, listed UNDER THE LOCATION ROWS in Reports, and only for a viewer who
 *     has at least one.
 */
export type PortalLibrarySection =
  | 'strategy.monthly'
  | 'strategy.alternate'
  | 'strategy.campaigns'
  | 'college'
  | 'reports.custom';

/** Labels for every section. A Record, so a new section cannot miss one. */
export const PORTAL_LIBRARY_SECTION_LABELS: Record<PortalLibrarySection, string> = {
  'strategy.monthly': 'Monthly campaigns',
  'strategy.alternate': 'Alternate campaigns',
  'strategy.campaigns': 'Campaigns & promotions',
  college: 'Kyck college',
  'reports.custom': 'Custom reports',
};

export const PORTAL_LIBRARY_SECTIONS = Object.keys(
  PORTAL_LIBRARY_SECTION_LABELS,
) as PortalLibrarySection[];

/**
 * ⚠️ A DISCRIMINATED UNION WITH ONE MEMBER, DELIBERATELY (brief §0 #1). Kyck
 * college will hold an equipment list or a video link long before it holds
 * only decks, and a bare `hostedReportId` field would make that a migration
 * over every row instead of a new branch.
 */
export type PortalLibraryContent = { kind: 'hostedHtml'; hostedReportId: string };

/**
 * Who a tile is for. Visibility = does ANY account in the viewer's resolved
 * PortalScope match ANY rule. ⚠️ An EMPTY rule list matches NOBODY.
 *
 * ⚠️⚠️ `marque` IS ITS OWN KIND and resolves through the canonical
 * `computeBrandAncestorMap` (Amendment 1 §D): a dealership's marque is an
 * explicit pointer, a franchise location's is its nearest brand ancestor.
 * Folding it into `accountSubtree` would match no dealership; reading
 * `brandAccountId` alone would match no franchise location.
 */
export type PortalAudienceRule =
  | { kind: 'everyone' }
  | { kind: 'industry'; industry: Industry }
  /** The account itself, or anything beneath it — covers Groups AND Accounts. */
  | { kind: 'accountSubtree'; accountId: string }
  | { kind: 'marque'; brandAccountId: string };

export type PortalLibraryEntry = {
  id: string;
  section: PortalLibrarySection;
  title: string;
  /** One or two lines under the title on the tile. */
  summary: string | null;
  content: PortalLibraryContent;
  audience: PortalAudienceRule[];
  /**
   * Normalised free text (trim, lowercase, collapsed whitespace), NOT ids —
   * Amendment 1 §C. Strings keep a controlled vocabulary possible later
   * without a migration.
   */
  tags: string[];
  /**
   * ⚠️⚠️ THE CATEGORY'S ID, NEVER ITS NAME (Round 4 §6) — the opposite of tags,
   * on purpose: renaming "Video" must not orphan anything. REQUIRED for
   * `college`, null for every other section — enforced in the schema, the way
   * `period` is. ⚠️ Legacy college entries read as null until someone edits
   * them; the tile then shows under "All" only.
   */
  categoryId: string | null;
  /**
   * The thumbnail's STORAGE object path, or null. Null is a DESIGNED state: the
   * tile renders typographically (brief §5). ⚠️ Never sent to a client as-is —
   * the client tile carries `thumbnailUrl`, our own serving route.
   */
  thumbnailPath: string | null;
  /** `yyyy-mm`. REQUIRED for `strategy.monthly`, null for every other section. */
  period: string | null;
  visibleFrom: IsoTimestamp;
  /**
   * null = the tile never comes down. ⚠️ The hosted report's `expiresAt`
   * FOLLOWS this date (brief §3c), including when both are null — the hosted
   * report carries this entry's id and refuses an independent expiry change.
   */
  visibleUntil: IsoTimestamp | null;
  createdAt: IsoTimestamp;
  /** A uid only — never a denormalised name (Amendment 2 §B). */
  createdBy: string;
  updatedAt: IsoTimestamp;
};

/**
 * Where a tile stands. ⚠️ Each state needs a DIFFERENT fix, so they are never
 * collapsed: revoked is a staff action on the file, expired is a clock, and a
 * missing report was deleted or purged.
 */
export type PortalLibraryTileState =
  | 'live'
  | 'scheduled'
  | 'ended'
  | 'reportRevoked'
  | 'reportExpired'
  | 'reportMissing';

/** A per-rule reach line — which rule matched how many accounts. */
export type PortalAudienceRuleReach = {
  rule: PortalAudienceRule;
  /** Resolved at read time from the live roster. */
  label: string;
  accountCount: number;
};

/**
 * "This will appear for N accounts across M brands" (Amendment 2 §D).
 * ⚠️ Operational accounts only (locations + dealerships) — the same set a
 * PortalScope's data scope is made of.
 */
export type PortalAudienceReach = {
  accountCount: number;
  brandCount: number;
  /**
   * Of `accountCount`, how many some portal user can see TODAY — resolved
   * through the client portal's own scope path, never a second definition of
   * access. ⚠️ Smaller than `accountCount` is normal: a tile reaches an account
   * the day someone there is invited, without being republished.
   */
  accountsWithPortalAccess: number;
  /**
   * The section this reach was computed for, and how many of `accountCount` have
   * it in their nav (Round 6 §3). ⚠️ Section and audience are two independent
   * choices that can contradict each other — Monthly campaigns aimed at
   * dealerships reaches nobody, with no error — so this is counted from the SAME
   * matched set, and zero is refused. `accountsWithPortalAccess` is a subset of
   * THIS, not of `accountCount`: an account that cannot see the section cannot
   * open the tile whatever its access.
   */
  section: PortalLibrarySection;
  accountsSeeingSection: number;
  perRule: PortalAudienceRuleReach[];
};

/**
 * What a CLIENT is given for a visible tile.
 *
 * ⚠️⚠️ NO `audience`, NO `createdBy`, and that is a leak guard rather than
 * tidiness: an `accountSubtree` rule names another client's account id, and a
 * `marque` or `industry` rule tells one franchisee who else a deck went to.
 * A separate type, so returning the stored entry by accident is a compile error
 * at the query's return rather than a field someone forgot to strip.
 */
export type PortalLibraryTile = {
  id: string;
  section: PortalLibrarySection;
  title: string;
  summary: string | null;
  /**
   * ⚠️ SENT FOR SEARCH ONLY (Round 4 §6) — tags are never rendered to a client.
   * The chip row is the CATEGORIES, a controlled list, because it is the first
   * thing a client reads and cannot be built from free text.
   */
  tags: string[];
  categoryId: string | null;
  /** Our own thumbnail route, or null for the typographic tile. */
  thumbnailUrl: string | null;
  period: string | null;
  // ⚠️ NO `url` (Round 3, 2026-09-24). A tile opens `/portal/library/:id`, which
  // re-checks the viewer every time. It used to carry the report's shareable
  // `/r/:token` link, and that link is what a client who left kept.
};

/**
 * A controlled Kyck college category (Round 4 §6). ⚠️ `order` is MANUAL:
 * alphabetical would put "Equipment" above "Getting started", and the chip row
 * is the first thing a client reads. Deleting one that entries use is refused.
 */
export type PortalLibraryCategory = {
  id: string;
  name: string;
  order: number;
  createdAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
};

/** The staff view of a category: how many entries use it (the delete guard's count). */
export type PortalLibraryCategoryRow = PortalLibraryCategory & { entryCount: number };

/** What a client sees of a category: a chip. Only categories a visible tile uses. */
export type PortalLibraryClientCategory = { id: string; name: string };

/** The client list response for one section. */
export type PortalLibrarySectionResult = {
  tiles: PortalLibraryTile[];
  /** In configured order; empty for every section but `college`. */
  categories: PortalLibraryClientCategory[];
};

/**
 * What the portal's entry endpoint answers when a signed-in client opens one
 * document. `contentPath` is the sandboxed content route the viewer iframes; it
 * serves the file only while the short-lived portal grant set alongside this
 * response is valid.
 *
 * ⚠️ `not_available` covers every refusal alike (not in the audience, no longer
 * showing, never existed) so the answer never confirms a document exists.
 */
export type PortalLibraryOpenResult =
  | { status: 'ok'; title: string; section: PortalLibrarySection; contentPath: string }
  | { status: 'not_available' };

/** A row on the STAFF library list. Never sent to a client. */
export type PortalLibraryStaffRow = {
  entry: PortalLibraryEntry;
  tileState: PortalLibraryTileState;
  reach: PortalAudienceReach;
  /** The file behind the tile, or null when that report no longer exists. */
  report: {
    id: string;
    title: string;
    url: string;
    state: HostedReportState;
    /** Denormalised on the hosted report — legitimate THERE (Amendment 2 §B). */
    createdByName: string;
    viewCount: number;
  } | null;
};
